import { indexBy } from 'remeda'
import {
  AssignmentRequest,
  DistributionHost,
  RoutingRules,
  TimeAndAssignmentV2WithReservation,
  TimeAndAssignmentV2WithUnresolvedAssignees,
} from '@chilipiper/api-client/src/team-scheduling'
import {
  RoundRobinAvailableAttendees,
  StrictRoundRobin1,
} from '@chilipiper/api-client/src/user-availability'
import {
  WithDistribution,
  OwnershipLinkRoutingResult,
  mapOwnershipLinkRoutingResultToAssignments,
  mapOwnershipLinkRoutingResultToAssignments2,
  isOwner,
  isMixedResults,
  isOwnerFromSimpleRule,
} from '../../load-meeting-attempt'

export type TimeAndAssignment =
  | TimeAndAssignmentV2WithUnresolvedAssignees
  | TimeAndAssignmentV2WithReservation

export type TimeAndAssignmentWithReservation = {
  hostId: string
  reservationId: string
  retryReservationId?: string
  strictRoundRobinAssignees?: StrictRoundRobin1[]
  roundRobinRoutingResult?: WithDistribution
  ownershipLinkRoutingResult?: OwnershipLinkRoutingResult
}

export type TimeAndAssignmentMultipleAssignments = {
  availableAssignees: RoundRobinAvailableAttendees[]
  meetingTypeId: string
  duration: string
  slotStartsAt: string
  roundRobinRoutingResult?: WithDistribution
  ownershipLinkRoutingResult?: OwnershipLinkRoutingResult
  host: DistributionHost
  priority?: 'highPriorityReservation' | null
  hostId?: never
  reservationId?: never
  retryReservationId?: never
}

export type CreateTimeAndAssignment =
  | TimeAndAssignmentWithReservation
  | TimeAndAssignmentMultipleAssignments

const isTimeAndAssignmentWithReservation = (
  props: CreateTimeAndAssignment
): props is Extract<CreateTimeAndAssignment, { reservationId: string }> => 'reservationId' in props

export const createGroupLinkTimeAndAssignment = (
  reservationId: string,
  hostId: string
): TimeAndAssignment => {
  return {
    type: 'WithReservation',
    hostId,
    reservationId,
    assignments: undefined,
    routingRules: undefined,
  }
}

export const createRoundRobinLinkTimeAndAssignment = (
  props: CreateTimeAndAssignment
): TimeAndAssignment => {
  if (isTimeAndAssignmentWithReservation(props)) {
    return {
      hostId: props.hostId,
      reservationId: props.retryReservationId ?? props.reservationId,
      assignments: mapsStrictAssignments(
        props.strictRoundRobinAssignees,
        props.roundRobinRoutingResult
      ),
      routingRules: undefined,
      type: 'WithReservation',
    } satisfies TimeAndAssignmentV2WithReservation
  }

  return {
    assignments: mapRRAssignees(props.availableAssignees, props.roundRobinRoutingResult),
    host: props.host,
    priority: props.priority === 'highPriorityReservation' ? 'High' : 'Default',
    interval: {
      duration: props.duration,
      startsAt: props.slotStartsAt,
    },
    meetingTypeReference: {
      meetingTypeId: props.meetingTypeId,
      timestamp: new Date().toISOString(),
    },
    routingRules: undefined,
    type: 'WithUnresolvedAssignees',
  } satisfies TimeAndAssignmentV2WithUnresolvedAssignees
}

export const createOwnershipLinkTimeAndAssignment = (
  props: CreateTimeAndAssignment
): TimeAndAssignment => {
  if (isTimeAndAssignmentWithReservation(props)) {
    return {
      hostId: props.hostId,
      reservationId: props.retryReservationId ?? props.reservationId,
      assignments: mapOwnershipLinkRoutingResultToAssignments(
        props.strictRoundRobinAssignees,
        props.ownershipLinkRoutingResult
      ),
      routingRules: getRoutingRulesFromRoutingResults(props.ownershipLinkRoutingResult),
      type: 'WithReservation',
    } satisfies TimeAndAssignmentV2WithReservation
  }

  return {
    assignments: mapOwnershipLinkRoutingResultToAssignments2(
      props.availableAssignees,
      props.ownershipLinkRoutingResult
    ),
    host: props.host,
    priority: props.priority === 'highPriorityReservation' ? 'High' : 'Default',
    interval: {
      duration: props.duration,
      startsAt: props.slotStartsAt,
    },
    meetingTypeReference: {
      meetingTypeId: props.meetingTypeId,
      timestamp: new Date().toISOString(),
    },
    routingRules: getRoutingRulesFromRoutingResults(props.ownershipLinkRoutingResult),
    type: 'WithUnresolvedAssignees',
  } satisfies TimeAndAssignmentV2WithUnresolvedAssignees
}

const mapsStrictAssignments = (
  availableAssignees?: StrictRoundRobin1[],
  roundRobinRoutingResult?: WithDistribution
) => {
  if (!roundRobinRoutingResult) {
    return []
  }

  if (!availableAssignees) {
    return []
  }

  // Create a map of available assignees by distributionId for quick lookup
  const mappedAvailableAssignees = indexBy(availableAssignees || [], item => item.distributionId)

  // Process distributions in the order they appear in roundRobinRoutingResult
  return roundRobinRoutingResult.distributions.flatMap(distribution => {
    const assignee = mappedAvailableAssignees[distribution.id]
    if (!assignee) {
      return []
    }

    return {
      assigneeId: assignee.userId,
      distributionId: assignee.distributionId,
      type: 'AssignedViaDistribution' as const,
    }
  })
}

const mapRRAssignees = (
  availableAssignees: RoundRobinAvailableAttendees[],
  roundRobinRoutingResult?: WithDistribution
): AssignmentRequest[] => {
  if (!roundRobinRoutingResult) {
    return []
  }

  // Create a map of available assignees by distributionId for quick lookup
  const mappedAvailableAssignees = indexBy(availableAssignees || [], item => item.distributionId)

  // Process distributions in the order they appear in roundRobinRoutingResult
  return roundRobinRoutingResult.distributions.flatMap(distribution => {
    const assignee = mappedAvailableAssignees[distribution.id]
    if (!assignee) {
      return []
    }

    if (assignee.type === 'StrictRoundRobin') {
      return {
        assigneeId: assignee.userId,
        distributionId: assignee.distributionId,
        type: 'AssignedViaDistribution' as const,
      }
    }
    return {
      availableUsers: assignee.userIds,
      distributionId: assignee.distributionId,
      type: 'WithUnresolvedDistribution' as const,
    }
  })
}

const getRoutingRulesFromRoutingResults = (
  routingResult?: OwnershipLinkRoutingResult
): RoutingRules | undefined => {
  if (!routingResult) {
    return undefined
  }

  if (isOwnerFromSimpleRule(routingResult)) {
    return { type: 'SimpleRule' }
  }

  if (isMixedResults(routingResult)) {
    const ruleIds: string[] = routingResult.result.reduce((acc, item) => {
      if (isOwner(item)) {
        acc.push(item.ruleId)
      }
      return acc
    }, [] as string[])

    if (ruleIds.length > 0) {
      return {
        type: 'SharedRules',
        ruleIds: ruleIds as [string, ...string[]],
      }
    }
  }

  return undefined
}
